import { SentryProject } from '@data/utils/types';

/** What the Sentry row edits. Each field is independent, and null means "not linked". */
export interface SentryLinkDraft {
  sentryOrgSlug: string | null;
  sentryProjectSlugs: string[];
  sentryBrowserProjectSlug: string | null;
}

/**
 * Seeds a picker's search from the product's name, so opening it shows the two or three projects — or
 * repositories — that belong to this product rather than every one the token can see. First word only,
 * lowercased and stripped to alphanumerics, because both Sentry slugs and repository names are prefixed
 * by the product: "QIT Online" -> "qit". Clearing the box shows everything, for one named otherwise.
 */
export const searchSeedFor = (productName: string): string =>
  (productName.trim().split(/\s+/)[0] ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Only projects that actually carry the metric, so an admin doesn't have to go into Sentry to find out
 * which of a product's projects has what. `keep` is the currently linked slugs: they stay in the list even
 * with no data, or an existing link would silently vanish from its own dropdown.
 */
export const projectsWithMetric = (
  projects: SentryProject[],
  metric: 'transactions' | 'sessions',
  keep: (string | null | undefined)[] = [],
): SentryProject[] =>
  projects.filter(project => {
    if (keep.includes(project.slug)) return true;
    return metric === 'transactions' ? project.transactionCount > 0 : project.sessionCount > 0;
  });

/**
 * Label showing what the project carries, since the count is the reason to choose one over another.
 *
 * The unit word is passed in rather than translated here: reaching for the i18n singleton would pull the
 * whole i18next setup into every module that imports this one, and into every test that mocks
 * react-i18next.
 */
export const projectLabel = (project: SentryProject, metric: 'transactions' | 'sessions', unit: string): string => {
  const count = metric === 'transactions' ? project.transactionCount : project.sessionCount;
  const where = `${project.slug} (${project.orgSlug})`;
  return count ? `${where} · ${count.toLocaleString('en-IE')} ${unit}` : where;
};

/**
 * Which project to suggest for the browser side, given the server project just chosen: the sibling with
 * the most sessions. Release health is usually on only one of a product's projects and which one differs
 * — qit sends sessions from qit-app, valcori from valcori-api. Undefined when no sibling has any, so a
 * wrong project is never linked silently.
 */
export const suggestBrowserProject = (serverSlug: string, projects: SentryProject[]): SentryProject | undefined => {
  const base = serverSlug.replace(/-(api|app)$/, '');
  return projects
    .filter(project => project.slug !== serverSlug && (project.slug === base || project.slug.startsWith(`${base}-`)))
    .filter(project => project.sessionCount > 0)
    .sort((a, b) => b.sessionCount - a.sessionCount)[0];
};
